import { apiClient } from '@/shared/lib/axios';
import type { User, CreateUserParams, UpdateUserParams, Role, Site, TrustedDevice } from '../types/user';

interface ApiResponse<T> {
  success: boolean;
  error?: string;
  user?: T;
  users?: T[];
  roles?: Role[];
  sites?: Site[];
  message?: string;
}

interface TrustedDevicesApiResponse {
  success: boolean;
  error?: string;
  trustedDevices?: TrustedDevice[];
}

interface CsrfTokenResponse {
  csrfToken: string;
}

/**
 * CSRFトークンを取得
 */
const getCsrfToken = async (): Promise<string> => {
  const response = await apiClient.get<CsrfTokenResponse>('/api/auth/csrf-token');
  return response.data.csrfToken;
};

/**
 * ユーザー管理APIサービス
 */
export const userApiService = {
  /**
   * ユーザー一覧を取得
   */
  async getAllUsers(): Promise<readonly User[]> {
    const response = await apiClient.get<ApiResponse<User>>('/api/users');

    if (response.data.success === false || response.data.users === undefined) {
      throw new Error(response.data.error ?? 'ユーザー一覧の取得に失敗しました');
    }

    return response.data.users;
  },

  /**
   * ユーザー詳細を取得
   */
  async getUserById(params: { readonly userId: string }): Promise<User> {
    const response = await apiClient.get<ApiResponse<User>>(`/api/users/${params.userId}`);

    if (response.data.success === false || response.data.user === undefined) {
      throw new Error(response.data.error ?? 'ユーザーの取得に失敗しました');
    }

    return response.data.user;
  },

  /**
   * ユーザーを作成
   */
  async createUser(params: CreateUserParams): Promise<User> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.post<ApiResponse<User>>(
      '/api/users',
      params,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false || response.data.user === undefined) {
      throw new Error(response.data.error ?? 'ユーザーの作成に失敗しました');
    }

    return response.data.user;
  },

  /**
   * ユーザーを更新
   */
  async updateUser(params: { readonly userId: string } & UpdateUserParams): Promise<User> {
    const { userId, ...updateData } = params;
    const csrfToken = await getCsrfToken();
    const response = await apiClient.put<ApiResponse<User>>(
      `/api/users/${userId}`,
      updateData,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false || response.data.user === undefined) {
      throw new Error(response.data.error ?? 'ユーザーの更新に失敗しました');
    }

    return response.data.user;
  },

  /**
   * ユーザーを削除
   */
  async deleteUser(params: { readonly userId: string }): Promise<void> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.delete<ApiResponse<never>>(
      `/api/users/${params.userId}`,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false) {
      throw new Error(response.data.error ?? 'ユーザーの削除に失敗しました');
    }
  },

  /**
   * ロール一覧を取得
   */
  async getAllRoles(): Promise<readonly Role[]> {
    const response = await apiClient.get<ApiResponse<never>>('/api/roles');

    if (response.data.success === false || response.data.roles === undefined) {
      throw new Error(response.data.error ?? 'ロール一覧の取得に失敗しました');
    }

    return response.data.roles;
  },

  /**
   * サイト一覧を取得
   */
  async getAllSites(): Promise<readonly Site[]> {
    const response = await apiClient.get<ApiResponse<never>>('/api/sites');

    if (response.data.success === false || response.data.sites === undefined) {
      throw new Error(response.data.error ?? 'サイト一覧の取得に失敗しました');
    }

    return response.data.sites;
  },

  /**
   * ユーザーの信頼済みデバイス一覧を取得
   */
  async getTrustedDevices(params: {
    readonly userId: string;
  }): Promise<readonly TrustedDevice[]> {
    const response = await apiClient.get<TrustedDevicesApiResponse>(
      `/api/users/${params.userId}/trusted-devices`
    );

    if (response.data.success === false || response.data.trustedDevices === undefined) {
      throw new Error(response.data.error ?? '信頼済みデバイス一覧の取得に失敗しました');
    }

    return response.data.trustedDevices;
  },

  /**
   * 信頼済みデバイスを削除
   */
  async deleteTrustedDevice(params: {
    readonly userId: string;
    readonly deviceId: number;
  }): Promise<void> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.delete<ApiResponse<never>>(
      `/api/users/${params.userId}/trusted-devices/${params.deviceId}`,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false) {
      throw new Error(response.data.error ?? '信頼済みデバイスの削除に失敗しました');
    }
  },
};
